import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumVesselTypeIcon } from "./enums/enumVesselTypeIcon.generated";

// Model Objects

export class VesselType extends ModelObjectWithDirtyCheck<VesselType> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Vessel Type Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    public isHidden: boolean = false;
    /**
    * Vessel Icon Type
    */
    public icon: EnumVesselTypeIcon = EnumVesselTypeIcon.cylindrocononical;
    public capabilities: (Guid)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.isHidden = jsonSerializedObject.isHidden;
          this.icon = EnumVesselTypeIcon.getByValue(jsonSerializedObject.icon, this.icon);
          this.capabilities = jsonSerializedObject.capabilities.map((v: any) => Guid.parseOrEmpty(v));
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.isHidden = this.isHidden;
        toRet.icon = this.icon.value;
        toRet.capabilities = this.capabilities.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "VesselType.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
    ];
}


export class VesselTypeService extends ServiceBase {

  public async getVesselTypes(vesselTypeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(VesselType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    vesselTypeIds:vesselTypeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.vesselTypes.map((v: any) => new VesselType(v));;
    return this.makeJsonRequest("vesselType/v1/getVesselTypes", req, false, extractor, opts);
  };

  public async getAllVesselTypes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(VesselType)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.vesselTypes.map((v: any) => new VesselType(v));;
    return this.makeJsonRequest("vesselType/v1/getAllVesselTypes", req, false, extractor, opts);
  };

  public async saveVesselType(vesselType: VesselType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    vesselType:vesselType.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("vesselType/v1/saveVesselType", req, true, extractor, opts);
  };

  public async setVesselTypeIsHidden(vesselTypeId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    vesselTypeId:vesselTypeId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("vesselType/v1/setVesselTypeIsHidden", req, true, extractor, opts);
  };
}

export const vesselTypeService = new VesselTypeService();